import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import BarWithPill from 'src/components/followUp/common/Bar/BarWithPill';
import ChartBackground from 'src/components/followUp/common/ChartBackground/ChartBackground';

import { styles } from 'src/components/followUp/common/Bar/index.styles';

type StackLabel = {
  name: string;
  color: string;
};

type Bar = {
  label: string;
  stacks: Array<{ stackLabel: string; value: number }>;
  comparisonValue?: string | number;
};

type BarWithColor = {
  label: string;
  stack: { stackLabel: string; value: number };
  color: string;
};

interface NpsDistributionChartProps {
  bars: Bar[];
  stackLabels: StackLabel[];
}

const NpsDistributionChart: FC<NpsDistributionChartProps> = ({
  bars,
  stackLabels,
}) => {
  const barsWithColors = useMemo(
    () =>
      bars.reduce<BarWithColor[]>((acc, bar) => {
        const stack = bar.stacks[0];
        if (!stack) return acc;

        const stackLabelConfig = stackLabels.find(
          ({ name }) => name === stack.stackLabel,
        );
        if (!stackLabelConfig) return acc;

        acc.push({
          label: bar.label,
          stack,
          color: stackLabelConfig.color,
        });
        return acc;
      }, []),
    [bars, stackLabels],
  );

  const totalValue = useMemo(
    () => barsWithColors.reduce((acc, bar) => acc + (bar.stack.value || 0), 0),
    [barsWithColors],
  );

  return (
    <Box sx={{ height: '100%', padding: 4, width: '100%' }}>
      <ChartBackground maxValue={100} isRelative hideGridLabels>
        <Stack direction="row" sx={{ height: '100%', gap: 1 }}>
          {barsWithColors.map(({ label, stack, color }) => {
            const percentage = getRelativeValue(stack.value, totalValue, 0);

            return (
              <Box key={label} sx={{ flexShrink: 1, ...styles.barContainer }}>
                <Box sx={styles.barData}>
                  <BarWithPill
                    type="vertical"
                    relativeSize={percentage}
                    borderPosition="Top"
                    color={color}
                    borderColor={color}
                    withGradient
                    borderWidth={4}
                    pill={{
                      label: `${percentage}%`,
                      color: 'secondary',
                      squared: true,
                    }}
                    toolTip={`n=${stack.value}`}
                  />
                </Box>
                <Typography variant="body3" sx={styles.barLabel}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </ChartBackground>
    </Box>
  );
};

export default NpsDistributionChart;
